/**
 * Ignore ▾ menu for a closed lead that came back on WhatsApp, shared by the
 * Today page and My Today: "Ignore" choices, then "Not a lead" (stops
 * automated WhatsApp). A likely sales pitch gets "Suggested" on Sales pitch.
 */
import React from 'react';
import { Box, Divider, Menu, MenuItem, Typography } from '@mui/material';
import { IGNORE_REASONS, IGNORE_SECTIONS } from '../utils/myTodayActions';

const GOLD = '#9d6f32';
const MUTED = '#6b6b6b';
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** The item looks like a sales pitch: highlight that choice. */
  suggestPitch?: boolean;
  onPick: (reasonKey: string) => void;
};

const IgnoreMenu = ({ anchorEl, onClose, suggestPitch = false, onPick }: Props) => (
  <Menu
    anchorEl={anchorEl}
    open={!!anchorEl}
    onClose={onClose}
    MenuListProps={{ dense: true, sx: { py: 0.5 } }}
    PaperProps={{ sx: { width: 260, borderRadius: 2.5, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' } }}
  >
    {IGNORE_SECTIONS.flatMap((sec, i) => [
      ...(i > 0 ? [<Divider key={`${sec.key}-div`} sx={{ my: 0.5 }} />] : []),
      <Typography
        key={sec.key}
        sx={{ px: 1.75, pt: 0.75, pb: 0.25, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: MUTED }}
      >
        {sec.title}
        {sec.note && <Box component="span" sx={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>{` · ${sec.note}`}</Box>}
      </Typography>,
      ...IGNORE_REASONS.filter((r) => r.section === sec.key).map((r) => {
        const suggested = suggestPitch && r.key === 'not-lead-pitch';
        return (
          <MenuItem
            key={r.key}
            onClick={() => onPick(r.key)}
            sx={{
              minHeight: 34, px: 1.75, py: 0.5, alignItems: 'flex-start', flexDirection: 'column',
              borderLeft: `3px solid ${suggested ? GOLD : 'transparent'}`,
              bgcolor: suggested ? '#faf6ef' : 'transparent',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#1a1a1a', flex: 1 }}>{r.label}</Typography>
              {suggested && (
                <Box component="span" sx={{ px: 0.75, borderRadius: 999, bgcolor: '#f3efe8', color: GOLD, fontSize: 10.5, fontWeight: 700, lineHeight: '18px' }}>
                  Suggested
                </Box>
              )}
            </Box>
            {r.hint && <Typography sx={{ fontFamily: FONT, fontSize: 11, color: MUTED, lineHeight: 1.3 }}>{r.hint}</Typography>}
          </MenuItem>
        );
      }),
    ])}
  </Menu>
);

export default IgnoreMenu;
